import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form,
  Row, Spinner, Toast, ToastContainer, Table,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaTimesCircle, FaTrash, FaUserPlus } from 'react-icons/fa';
import { assignReviewSet, getSetTemplates } from '@/api/reviewSetApi';
import type {
  AssignReviewSetRequest,
  ReviewSetItemAssignmentDto,
  ReviewSetOwnerDto,
  ReviewSetTemplateSummaryDto,
  ReviewSetTemplateItemDto,
} from '@/types/reviews';
import { getSetTemplateDetail } from '@/api/reviewSetApi';

let _ownerKey = 0;
const nextOwnerKey = () => ++_ownerKey;

type OwnerRow = ReviewSetOwnerDto & { _key: number };

const AssignReviewSet: React.FC = () => {
  const navigate = useNavigate();

  const [setTemplates, setSetTemplates] = useState<ReviewSetTemplateSummaryDto[]>([]);
  const [selectedSetTemplateGuid, setSelectedSetTemplateGuid] = useState('');
  const [templateItems, setTemplateItems] = useState<ReviewSetTemplateItemDto[]>([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const [setInstanceName, setSetInstanceName] = useState('');
  const [setInstanceDescription, setSetInstanceDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reviewConfig, setReviewConfig] = useState('');

  const [owners, setOwners] = useState<OwnerRow[]>([
    { _key: nextOwnerKey(), ownerEmail: '', isPrimary: true },
  ]);
  const [itemAssignments, setItemAssignments] = useState<Record<string, string>>({}); // itemGuid -> assigneeEmail

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState('');
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSetTemplates();
        setSetTemplates(data);
      } catch {
        // non-fatal
      }
    })();
  }, []);

  const loadTemplateItems = useCallback(async (guid: string) => {
    if (!guid) {
      setTemplateItems([]);
      setItemAssignments({});
      return;
    }
    try {
      setLoadingTemplate(true);
      const detail = await getSetTemplateDetail(guid);
      setTemplateItems(detail.items);
      // Clear assignments so they default to primary owner
      setItemAssignments({});
    } catch {
      setTemplateItems([]);
    } finally {
      setLoadingTemplate(false);
    }
  }, []);

  const handleTemplateChange = (guid: string) => {
    setSelectedSetTemplateGuid(guid);
    loadTemplateItems(guid);
    // Pre-fill instance name if blank
    const tpl = setTemplates.find((t) => t.setTemplateGuid === guid);
    if (tpl && !setInstanceName.trim()) {
      const year = new Date().getFullYear();
      setSetInstanceName(`${tpl.setTemplateName} ${year}`);
    }
  };

  // ---- Owners ----
  const addOwner = () => {
    setOwners((prev) => [...prev, { _key: nextOwnerKey(), ownerEmail: '', isPrimary: false }]);
  };

  const removeOwner = (key: number) => {
    setOwners((prev) => {
      const next = prev.filter((o) => o._key !== key);
      // If we removed the primary, promote the first remaining
      if (next.length > 0 && !next.some((o) => o.isPrimary)) {
        next[0].isPrimary = true;
      }
      return next;
    });
  };

  const updateOwnerEmail = (key: number, email: string) => {
    setOwners((prev) => prev.map((o) => (o._key === key ? { ...o, ownerEmail: email } : o)));
  };

  const setPrimary = (key: number) => {
    setOwners((prev) => prev.map((o) => ({ ...o, isPrimary: o._key === key })));
  };

  // ---- Per-item assignee overrides ----
  const updateItemAssignment = (itemGuid: string, email: string) => {
    setItemAssignments((prev) => ({ ...prev, [itemGuid]: email }));
  };

  const primaryOwnerEmail = owners.find((o) => o.isPrimary)?.ownerEmail ?? '';

  // ---- Submit ----
  const handleAssign = async () => {
    setError(null);

    if (!selectedSetTemplateGuid) {
      setError('Please select a review set template.');
      return;
    }
    if (!setInstanceName.trim()) {
      setError('Set name is required.');
      return;
    }
    if (owners.some((o) => !o.ownerEmail.trim())) {
      setError('All owner entries must have a valid email address.');
      return;
    }
    if (!owners.some((o) => o.isPrimary)) {
      setError('At least one owner must be marked as primary.');
      return;
    }

    const builtItemAssignments: ReviewSetItemAssignmentDto[] = templateItems
      .filter((item) => itemAssignments[item.itemGuid]?.trim())
      .map((item) => ({
        setTemplateItemGuid: item.itemGuid,
        assigneeEmail: itemAssignments[item.itemGuid].trim().toLowerCase(),
      }));

    const request: AssignReviewSetRequest = {
      setTemplateGuid: selectedSetTemplateGuid,
      setInstanceName: setInstanceName.trim(),
      setInstanceDescription: setInstanceDescription.trim() || undefined,
      owners: owners.map((o) => ({ ownerEmail: o.ownerEmail.trim().toLowerCase(), isPrimary: o.isPrimary })),
      itemAssignments: builtItemAssignments,
      dueDate: dueDate || undefined,
      reviewConfig: reviewConfig.trim() || undefined,
      sessionGuid: localStorage.getItem('sessionGuid') ?? undefined,
    };

    try {
      setSaving(true);
      const result = await assignReviewSet(request);
      setToastMsg(`Review set assigned. Instance: ${result.setInstanceGuid}`);
      setShowToast(true);
      setTimeout(() => navigate('/admin/review-sets'), 1200);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Assignment failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="mt-4 pb-5">
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast bg="success" show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
          <Toast.Body className="text-white fw-semibold">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="mb-3">
        <Button variant="link" className="p-0 text-secondary" onClick={() => navigate('/admin/review-sets')}>
          ← Review Sets
        </Button>
      </div>
      <h4 className="mb-4">Assign Review Set</h4>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Set Template */}
      <Card className="mb-4">
        <Card.Header><strong>1. Choose Set Template</strong></Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Review Set Template <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={selectedSetTemplateGuid}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="">— select a set template —</option>
              {setTemplates.map((t) => (
                <option key={t.setTemplateGuid} value={t.setTemplateGuid}>
                  {t.setTemplateName} ({t.setTemplateCategory}) — {t.itemCount} review{t.itemCount !== 1 ? 's' : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {loadingTemplate && <Spinner size="sm" />}

          {templateItems.length > 0 && (
            <div className="small text-secondary">
              This set includes:{' '}
              {templateItems.map((i, idx) => (
                <span key={i.itemGuid}>
                  {i.reviewTemplateName}
                  {i.isRequired ? '' : ' (optional)'}
                  {idx < templateItems.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Instance Details */}
      <Card className="mb-4">
        <Card.Header><strong>2. Set Details</strong></Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  value={setInstanceName}
                  onChange={(e) => setSetInstanceName(e.target.value)}
                  placeholder="e.g. Annual Site Review 2026 – Site X"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={setInstanceDescription}
                  onChange={(e) => setSetInstanceDescription(e.target.value)}
                  placeholder="Optional context for reviewers"
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Review Config{' '}
                  <span className="text-secondary small fw-normal">(JSON — siteUrl, groupId, etc. Passed to all child reviews.)</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={reviewConfig}
                  onChange={(e) => setReviewConfig(e.target.value)}
                  placeholder='{"siteUrl": "https://tenant.sharepoint.com/sites/example", "groupId": "..."}'
                  spellCheck={false}
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Owners */}
      <Card className="mb-4">
        <Card.Header>
          <strong>3. Owners</strong>
          <span className="text-secondary small ms-2 fw-normal">
            All group owners who are responsible for this review set. One must be primary.
          </span>
        </Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Email</th>
                <th style={{ width: 100 }}>Primary</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {owners.map((owner) => (
                <tr key={owner._key}>
                  <td>
                    <Form.Control
                      size="sm"
                      value={owner.ownerEmail}
                      onChange={(e) => updateOwnerEmail(owner._key, e.target.value)}
                      placeholder="user@domain.com"
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="radio"
                      name="primaryOwner"
                      checked={owner.isPrimary}
                      onChange={() => setPrimary(owner._key)}
                      label={owner.isPrimary ? <Badge bg="info" text="dark">Primary</Badge> : 'Set primary'}
                    />
                  </td>
                  <td>
                    {owners.length > 1 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={() => removeOwner(owner._key)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer>
          <Button variant="outline-secondary" size="sm" onClick={addOwner}>
            <FaUserPlus className="me-1" />
            Add Owner
          </Button>
        </Card.Footer>
      </Card>

      {/* Per-item Assignments */}
      {templateItems.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <strong>4. Assign Individual Reviews</strong>
            <span className="text-secondary small ms-2 fw-normal">
              Each review defaults to the primary owner. Override here to divide work.
            </span>
          </Card.Header>
          <Card.Body className="p-0">
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Review</th>
                  <th>Assignee</th>
                </tr>
              </thead>
              <tbody>
                {templateItems.map((item, idx) => (
                  <tr key={item.itemGuid}>
                    <td className="text-secondary">{idx + 1}</td>
                    <td>
                      <span className="fw-semibold">{item.reviewTemplateName}</span>
                      <span className="text-secondary small ms-2">{item.reviewTemplateCategory}</span>
                      {!item.isRequired && (
                        <Badge bg="secondary" className="ms-1">Optional</Badge>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={itemAssignments[item.itemGuid] ?? ''}
                        onChange={(e) => updateItemAssignment(item.itemGuid, e.target.value)}
                        placeholder={primaryOwnerEmail || 'primary owner'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Actions */}
      <div className="d-flex gap-2">
        <Button variant="primary" onClick={handleAssign} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : <FaSave className="me-1" />}
          Assign Review Set
        </Button>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/review-sets')} disabled={saving}>
          <FaTimesCircle className="me-1" />
          Cancel
        </Button>
      </div>
    </Container>
  );
};

export default AssignReviewSet;
